/**
 * External Imports
 */
import { differenceInHours, parse } from 'date-fns';

/**
 * Internal Imports
 */
import { date as dt } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  Employee,
  Patient,
  RoundHistory,
  Task
} from '../../../config/interfaces';

// TODO: All of these need better typedefs between response contract and cleaned type
export const cleanIssue = (task: Task) => {
  let taskStatus;
  if (task.completed) {
    taskStatus = 'Completed';
  } else {
    taskStatus = 'Opened';
  }
  return {
    friendlyId: task.friendlyId,
    taskId: task.taskId,
    userId: task.userId,
    taskType: task.taskType,
    category: task.category,
    taskSubtype: task.taskSubtype,
    subCategory: task.subCategory,
    taskCategory: task.taskCategory,
    title: task.title,
    completed: task.completed,
    completedDate: task.completedDate,
    taskDate: task.taskDate,
    description: task.description,
    facilityId: task.facilityId,
    patientFirstName: task.patientFirstName,
    patientLastName: task.patientLastName,
    patientFullName: task.patientFullName,
    employeeFirstName: task.employeeFirstName,
    employeeLastName: task.employeeLastName,
    employeeFullName: task.employeeFullName,
    employeeId: (task.employeeId as string)?.toUpperCase(),
    room: task.room,
    bed: task.bed,
    roundingType: task.roundingType,
    sharedWith: task.sharedWith,
    spotlight: task.isCopiedToStoplight,
    taskStatus,
    updated: task.updated,
    updatedBy: task.updatedBy,
    unitId: task.unitId,
    userFullName: task.userFullName,
    stoplightId: task.stoplightId,
    ticketNumber: task.ticketNumber,
    latestComment: task.latestComment,
    discussion: task.discussion,
    discussionId: task.discussionId,
    department: task.department,
    departmentCode: task.departmentCode,
    departmentId: task.departmentId,
    callback: task.callback || task.u_best_contact_info,
    u_best_contact_info: task.u_best_contact_info,
    u_issue_location: task.u_issue_location,
    u_control_number: task.u_control_number,
    ec_loc: task.ec_loc,
    roundId: task.roundId,
    accountNum: task.accountNum
  };
};

export const cleanFeedback = (task: Task) => {
  let taskStatus;
  if (task.completed) {
    taskStatus = 'Completed';
  } else {
    taskStatus = 'Opened';
  }
  return {
    friendlyId: task.friendlyId,
    taskId: task.taskId,
    userId: task.userId,
    taskType: task.taskType,
    category: task.category,
    taskSubtype: task.taskSubtype,
    subCategory: task.subCategory,
    taskCategory: task.taskCategory,
    title: task.title,
    completed: task.completed,
    completedDate: task.completedDate,
    taskDate: task.taskDate,
    description: task.description,
    facilityId: task.facilityId,
    patientFirstName: task.patientFirstName,
    patientLastName: task.patientLastName,
    patientFullName: task.patientFullName,
    employeeFirstName: task.employeeFirstName,
    employeeLastName: task.employeeLastName,
    employeeFullName: task.employeeFullName,
    employeeId: (task.employeeId as string)?.toUpperCase(),
    room: task.room,
    bed: task.bed,
    roundingType: task.roundingType,
    sharedWith: task.sharedWith,
    spotlight: task.isCopiedToStoplight,
    taskStatus,
    updated: task.updated,
    updatedBy: task.updatedBy,
    unitId: task.unitId,
    userFullName: task.userFullName,
    stoplightId: task.stoplightId,
    ticketNumber: task.ticketNumber,
    latestComment: task.latestComment,
    discussion: task.discussion,
    discussionId: task.discussionId,
    department: task.department,
    departmentCode: task.departmentCode,
    departmentId: task.departmentId,
    callback: task.callback || task.u_best_contact_info,
    u_best_contact_info: task.u_best_contact_info,
    u_issue_location: task.u_issue_location,
    u_control_number: task.u_control_number,
    ec_loc: task.ec_loc,
    roundId: task.roundId,
    accountNum: task.accountNum
  };
};

export const cleanPatientToRound = (patient: Patient) => {
  if (patient.admitTime) {
    const today = new Date();
    const admitTime = patient?.admitTime
      ? patient.admitTime.length === 12
        ? parse(patient?.admitTime, 'yyyyMMddHHmm', new Date())
        : patient.admitTime.length === 14
        ? parse(patient?.admitTime, 'yyyyMMddHHmmss', new Date())
        : undefined
      : undefined;
    const lastRoundDate =
      patient?.lastRound?.roundDate &&
      ((dt.isOrbitTimestamp(patient?.lastRound?.roundDate) &&
        `${dt.dayOfYr(patient.lastRound.roundDate)} @ ${dt.localTimeOfDay(
          patient.lastRound.roundDate
        )}`) ||
        'Unknown');

    let roundStatus;
    const lengthOfStay = admitTime ? differenceInHours(today, admitTime) : -1;
    if (patient.completed) {
      roundStatus = 'Complete';
    } else {
      roundStatus = 'To Do';
    }
    const fullName = `${patient.firstName} ${patient.lastName}`;

    return {
      ...patient,
      roundStatus,
      lastRoundDate: patient?.lastRound?.roundDate,
      lastRoundedBy: patient?.lastRound?.roundedBy,
      formattedLastRoundDate: lastRoundDate,
      lengthOfStay,
      fullName
    };
  }
  const fullName = `${patient.firstName} ${patient.lastName}`;

  return {
    ...patient,
    roundStatus: 'To Do',
    lastRoundDate: null,
    lastRoundedBy: null,
    formattedLastRoundDate: null,
    fullName
  };
};

export const cleanUnlistedPatientToRound = (history: RoundHistory) => {
  return {
    roundStatus:
      history.completed &&
      history?.roundDate &&
      dt.isOrbitTimestamp(history?.roundDate) &&
      dt.isToday(dt.parseDate(history?.roundDate))
        ? 'Complete'
        : 'To Do',
    accountNum: history.accountNum,
    mrn: history.patientMRN,
    urn: history.patientId,
    encounterId: '',
    unitId: history.unitId,
    dateOfBirth: '',
    firstName: history.patientFirstName?.toUpperCase(),
    lastName: history.patientLastName?.toUpperCase(),
    room: history.room,
    bed: history.bed,
    admitTime: '',
    dischargeTime: '',
    patientStatus: '',
    transferred: false,
    sex: '',
    chiefComplaint: '',
    attendingPhysician: '',
    notes: [],
    lastRoundId: history.roundId,
    lastRoundDate: history.roundDate,
    formattedLastRoundDate: `${dt.dayOfYr(
      history?.roundDate
    )} @ ${dt.localTimeOfDay(history?.roundDate)}`,
    lengthOfStay: -1,
    lastRoundedBy: `${history.userFirstName} ${history.userLastName}`
  };
};

export const cleanEmployeeToRound = (employee: Employee) => {
  return {
    ...employee,
    fullName: `${employee.firstName} ${employee.lastName}`,
    lastRoundDate: employee?.lastRound?.roundDate,
    lastRoundedBy: employee?.lastRound?.roundedBy || 'Unknown'
  };
};

export const cleanEmpDepartment = (dept: any) => ({
  departmentName: dept.name,
  departmentId: dept.id
});
